// =========================================================================
// Registro inicial · sello e inmutabilidad
//
// Decisión de Carlos (9 de agosto de 2026):
//   Los datos declarados se REVISAN antes de enviarse a calcular. La persona
//   puede corregir errores de tecleo EN EL MOMENTO de la inscripción, pero no
//   después. Pasado ese punto, solo se corrigen con autorización del
//   administrador, para evitar la manipulación del dato.
//
// Por qué importa: el peso y la talla declarados son la base de todo lo que
// el sistema calcula después. Si se pueden reescribir libremente, cualquier
// progresión se puede fabricar hacia atrás y el histórico deja de significar
// nada. El sello es lo que convierte una declaración en un dato.
//
// Lo que NO hace este módulo: impedirlo de verdad. Vive en el cliente, y un
// cliente no impone nada. La imposición es del servidor; esto es la mitad de
// la puerta que se ve. Ver `services/helpydoc-backend`.
// =========================================================================
import type { Sexo } from './objetivos';

/** Lo que la persona declara en la inscripción. */
export interface DatosDeclarados {
  sexo?: Sexo;
  pesoKg?: number;
  tallaCm?: number;
  cinturaCm?: number;
  comorbilidades: string[];
}

/**
 * Autorización de un administrador para tocar un dato ya sellado.
 * `administrador` y `motivo` son obligatorios: una corrección sin autor ni
 * razón es exactamente la manipulación que el sello existe para impedir.
 */
export interface AutorizacionAdmin {
  administrador: string;
  /** ISO 8601. */
  fecha: string;
  motivo: string;
}

/** Rastro de un cambio aplicado sobre un registro ya sellado. */
export interface Correccion {
  campo: keyof DatosDeclarados;
  antes: unknown;
  despues: unknown;
  autorizacion: AutorizacionAdmin;
}

export interface RegistroInicial {
  datos: DatosDeclarados;
  /**
   * Presencia = sellado EN LOCAL. Mientras sea undefined la persona puede
   * corregir libremente, que es el momento de la inscripción.
   */
  confirmadoEn?: string;
  /**
   * Presencia = el SERVIDOR confirmó la recepción (`POST /valoracion`, 200 o
   * 409 «ya sellado» — las dos son éxito). Sellado en local no es sellado de
   * verdad: la línea roja del TCA la impone el servidor sobre lo que recibe,
   * y mientras este campo falte, el dato puede no haber llegado nunca —
   * ver `sincronizacion-valoracion.ts`, que reintenta hasta que llegue.
   */
  sincronizadoEn?: string;
  /** Historial completo. No se sobrescribe nunca: solo se le añade. */
  correcciones: Correccion[];
}

export const REGISTRO_VACIO: RegistroInicial = { datos: { comorbilidades: [] }, correcciones: [] };

export function estaSellado(r: RegistroInicial): boolean {
  return typeof r.confirmadoEn === 'string' && r.confirmadoEn.length > 0;
}

export function estaSincronizado(r: RegistroInicial): boolean {
  return typeof r.sincronizadoEn === 'string' && r.sincronizadoEn.length > 0;
}

/**
 * Marca que el servidor ya confirmó la recepción. No hace nada si el registro
 * todavía no está sellado en local: sincronizar algo que la persona ni
 * siquiera ha confirmado no tiene sentido y sería un estado imposible.
 */
export function marcarSincronizado(r: RegistroInicial, ahora: string): RegistroInicial {
  if (!estaSellado(r)) return r;
  return { ...r, sincronizadoEn: ahora };
}

/**
 * Guarda mientras se rellena. Si ya está sellado NO hace nada: es el punto
 * exacto que la regla protege, y devolver el registro intacto en vez de lanzar
 * evita que un componente descuidado borre un dato sellado por accidente.
 */
export function guardarBorrador(r: RegistroInicial, datos: DatosDeclarados): RegistroInicial {
  if (estaSellado(r)) return r;
  return { ...r, datos };
}

/**
 * Cierra el registro. Sellar dos veces NO mueve la fecha original: el momento
 * de la inscripción ocurre una sola vez, y si se pudiera repisar, el sello no
 * probaría cuándo se declaró nada.
 */
export function confirmar(r: RegistroInicial, datos: DatosDeclarados, ahora: string): RegistroInicial {
  if (estaSellado(r)) return r;
  return { ...r, datos, confirmadoEn: ahora };
}

/** Una autorización solo cuenta si lleva administrador y motivo. */
export function autorizacionValida(a: AutorizacionAdmin | undefined): boolean {
  return !!a && a.administrador.trim().length > 0 && a.motivo.trim().length > 0;
}

export type ResultadoCorreccion =
  | { ok: true; registro: RegistroInicial; aplicadas: Correccion[] }
  | { ok: false; motivo: 'no-sellado' | 'autorizacion-invalida' | 'sin-cambios' };

/**
 * Corrige un registro YA SELLADO. Devuelve un resultado en vez de lanzar para
 * que quien llame tenga que mirar si funcionó: un `catch` olvidado dejaría
 * pasar una corrección no autorizada en silencio.
 *
 * Cada campo cambiado deja su propia entrada con el antes y el después. Sin
 * ese rastro, «autorizado por el administrador» es una afirmación que nadie
 * puede comprobar después.
 */
export function corregir(
  r: RegistroInicial,
  cambios: Partial<DatosDeclarados>,
  autorizacion: AutorizacionAdmin,
): ResultadoCorreccion {
  // Antes del sello no hace falta autorización: se corrige y ya. Llamar aquí
  // en ese caso es un error de uso, y se dice.
  if (!estaSellado(r)) return { ok: false, motivo: 'no-sellado' };
  if (!autorizacionValida(autorizacion)) return { ok: false, motivo: 'autorizacion-invalida' };

  const aplicadas: Correccion[] = [];
  const datos: DatosDeclarados = { ...r.datos };

  for (const clave of Object.keys(cambios) as (keyof DatosDeclarados)[]) {
    const despues = cambios[clave];
    if (despues === undefined) continue;
    const antes = r.datos[clave];
    // Comparación por valor: `comorbilidades` es un array y dos listas iguales
    // no son el mismo objeto. Sin esto, reenviar el formulario sin tocar nada
    // generaría correcciones fantasma en el historial.
    if (JSON.stringify(antes) === JSON.stringify(despues)) continue;
    aplicadas.push({ campo: clave, antes, despues, autorizacion });
    (datos as unknown as Record<string, unknown>)[clave] = despues;
  }

  if (aplicadas.length === 0) return { ok: false, motivo: 'sin-cambios' };

  return {
    ok: true,
    registro: { ...r, datos, correcciones: [...r.correcciones, ...aplicadas] },
    aplicadas,
  };
}
